import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill, { PillTone } from './ui/Pill';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { ErrorNotice } from './ui/States';
import { dateLong } from '../lib/format';

interface Step {
  system: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  at: string;
  detail?: unknown;
  error?: string;
}

interface Operation {
  id: string;
  type: string;
  trigger: string;
  status: 'running' | 'succeeded' | 'partial' | 'failed';
  input: Record<string, unknown> | null;
  steps: Step[];
  error: string | null;
  startedAt: string;
}

const SYSTEM_LABEL: Record<string, string> = {
  ibm: 'IBM',
  sendgrid: 'Email',
  platform: 'Platform',
  mentorship: 'Mentorship',
  moodle: 'Moodle',
};

const STATUS_TONE: Record<Operation['status'], PillTone> = {
  succeeded: 'healthy',
  partial: 'running',
  failed: 'failed',
  running: 'running',
};

function operationTitle(op: Operation) {
  switch (op.type) {
    case 'provision':
      return 'Onboarding';
    case 'password_reset':
      return 'New password sent';
    case 'email_change':
      return `Email change → ${String(op.input?.newEmail ?? '')}`;
    case 'set_active':
      return op.input?.active ? 'Activated' : 'Deactivated';
    case 'merge':
      return 'Duplicate record merged in';
    case 'ibm_sync_update':
      return 'Updated from IBM';
    case 'ibm_deleted':
      return 'IBM account found deleted';
    case 'name_change':
      return `Name changed → ${String((op.input?.after as Record<string, string> | undefined)?.firstName ?? '')} ${String(
        (op.input?.after as Record<string, string> | undefined)?.lastName ?? '',
      )}`;
    case 'archive':
      return `Archived — ${String(op.input?.reason ?? '')}`;
    case 'missed_registration':
      return 'Registration arrived by nightly pull — no IBM account yet';
    case 'dedupe':
      return 'Duplicate person resolved';
    case 'not_duplicate':
      return 'Marked not the same person as a possible duplicate';
    case 'provision_held':
      return 'Onboarding held — possible duplicate person';
    case 'group_change':
      return `${op.input?.action === 'removed' ? 'Removed from' : 'Added to'} group ${String(op.input?.group ?? '')}`;
    default:
      return op.type;
  }
}

function errorMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { error?: string; data?: { error?: string } } } })?.response?.data;
  return data?.error ?? data?.data?.error ?? fallback;
}

export default function MemberAccount({
  userId,
  email,
  firstName,
  lastName,
  isActive,
  ibmLinked,
  ibmDeletedAt,
  archivedAt,
  mutable,
}: {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  ibmLinked: boolean;
  ibmDeletedAt: string | null;
  archivedAt: string | null;
  mutable: boolean;
}) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<null | 'email' | 'password' | 'active' | 'name' | 'archive'>(null);
  const [newEmail, setNewEmail] = useState('');
  const [names, setNames] = useState({ firstName, lastName });
  const [reason, setReason] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries(['member-360', userId]);
    queryClient.invalidateQueries(['member-operations', userId]);
  };

  const operations = useQuery<Operation[]>(
    ['member-operations', userId],
    async () => (await api.get(`/users/${userId}/operations`)).data.data,
  );

  const changeEmail = useMutation(
    async () => (await api.post(`/users/${userId}/email-change`, { newEmail })).data.data,
    { onSettled: refresh },
  );
  const retry = useMutation(
    async (operationId: string) => (await api.post(`/users/${userId}/operations/${operationId}/retry`)).data.data,
    { onSettled: refresh },
  );
  const resetPassword = useMutation(async () => (await api.post(`/users/${userId}/reset-password`)).data.data, {
    onSettled: refresh,
  });
  const setActive = useMutation(
    async () => {
      const res = await api.post('/users/active', { userIds: [userId], active: !isActive });
      const result = res.data.data.results[0];
      if (!result.ok) throw new Error(result.error);
      return result;
    },
    { onSettled: refresh },
  );

  const resultOrThrow = (data: { ok: boolean; error?: string }) => {
    if (!data.ok) throw new Error(data.error);
    return data;
  };
  const changeName = useMutation(
    async () => resultOrThrow((await api.post(`/users/${userId}/name`, names)).data.data),
    { onSettled: refresh },
  );
  const archive = useMutation(
    async () => resultOrThrow((await api.delete(`/users/${userId}`, { data: { reason } })).data.data),
    { onSettled: refresh },
  );

  const close = () => {
    setDialog(null);
    setNewEmail('');
    setNames({ firstName, lastName });
    setReason('');
    changeName.reset();
    archive.reset();
    changeEmail.reset();
    resetPassword.reset();
    setActive.reset();
  };

  return (
    <>
      {mutable ? (
        <Panel>
          <PanelHeader title="Account" />
          <PanelBody className="pt-1 flex flex-col gap-3">
            {ibmDeletedAt ? (
              <ErrorNotice>
                The IBM account linked to this member was found deleted on {dateLong(ibmDeletedAt)}. They cannot sign in
                until it is restored or the member is provisioned again.
              </ErrorNotice>
            ) : null}
            {archivedAt ? (
              <ErrorNotice>
                Archived on {dateLong(archivedAt)}: the IBM account was deleted and the member can no longer sign in.
                Their history is kept.
              </ErrorNotice>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setDialog('name')}>
                Edit name
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDialog('email')}>
                Change email
              </Button>
              {ibmLinked ? (
                <Button size="sm" variant="outline" onClick={() => setDialog('password')}>
                  Send new password
                </Button>
              ) : null}
              <Button size="sm" variant={isActive ? 'destructive' : 'outline'} onClick={() => setDialog('active')}>
                {isActive ? 'Deactivate' : 'Activate'}
              </Button>
              {!archivedAt ? (
                <Button size="sm" variant="destructive" onClick={() => setDialog('archive')}>
                  Archive
                </Button>
              ) : null}
            </div>
          </PanelBody>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader title="Activity log" />
        <PanelBody className="pt-1">
          {operations.isError ? <ErrorNotice>Could not load the activity log.</ErrorNotice> : null}
          {operations.data && operations.data.length === 0 ? (
            <p className="text-small text-ink-2 py-2">No account operations recorded yet.</p>
          ) : null}
          {(operations.data ?? []).map((op) => (
            <div key={op.id} className="py-[12px] border-b border-line last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-table font-semibold break-words">{operationTitle(op)}</div>
                  <div className="text-small text-ink-2 mt-[2px]">
                    {dateLong(op.startedAt)} · {op.trigger}
                  </div>
                </div>
                <Pill tone={STATUS_TONE[op.status]}>{op.status.charAt(0).toUpperCase() + op.status.slice(1)}</Pill>
              </div>
              <ul className="mt-2 flex flex-col gap-[3px]">
                {op.steps.map((s, i) => (
                  <li key={i} className="text-small text-ink-2">
                    <span className={s.status === 'failed' ? 'text-warning font-semibold' : ''}>
                      {SYSTEM_LABEL[s.system] ?? s.system}: {s.status}
                    </span>
                    {s.error ? ` — ${s.error}` : typeof s.detail === 'string' ? ` — ${s.detail}` : ''}
                  </li>
                ))}
              </ul>
              {mutable && op.type === 'email_change' && op.status === 'partial' ? (
                <div className="mt-2">
                  <Button size="sm" variant="outline" disabled={retry.isLoading} onClick={() => retry.mutate(op.id)}>
                    {retry.isLoading ? 'Retrying…' : 'Retry remaining steps'}
                  </Button>
                  {retry.isError ? (
                    <div className="mt-2">
                      <ErrorNotice>{errorMessage(retry.error, 'Retry failed.')}</ErrorNotice>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </PanelBody>
      </Panel>

      {dialog === 'email' ? (
        <Modal
          title="Change email"
          onClose={close}
          footer={
            changeEmail.data?.ok ? (
              <Button variant="primary" onClick={close}>
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  disabled={!newEmail || changeEmail.isLoading}
                  onClick={() => changeEmail.mutate()}
                >
                  {changeEmail.isLoading ? 'Changing…' : 'Change everywhere'}
                </Button>
                <Button variant="ghost" onClick={close}>
                  Cancel
                </Button>
              </>
            )
          }
        >
          <p className="text-table">
            Moves <strong>{email}</strong> to a new address in IBM (sign-in), the mentorship app, Moodle and this
            platform, in that order. Confirm the request really came from the member before continuing.
          </p>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="new address"
            disabled={changeEmail.isLoading || changeEmail.data?.ok}
            className="mt-4 w-full border border-line-strong px-3 py-2 text-table"
          />
          {changeEmail.data?.ok ? (
            <p className="text-small mt-4">Done. The member signs in with the new address from now on.</p>
          ) : null}
          {changeEmail.isError ? (
            <div className="mt-4">
              <ErrorNotice>
                {errorMessage(changeEmail.error, 'The change did not go through.')} Any step that completed is listed in
                the activity log; a partial change can be retried from there.
              </ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {dialog === 'password' ? (
        <Modal
          title="Send new password"
          onClose={close}
          footer={
            resetPassword.isSuccess ? (
              <Button variant="primary" onClick={close}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="primary" disabled={resetPassword.isLoading} onClick={() => resetPassword.mutate()}>
                  {resetPassword.isLoading ? 'Sending…' : 'Set and send'}
                </Button>
                <Button variant="ghost" onClick={close}>
                  Cancel
                </Button>
              </>
            )
          }
        >
          <p className="text-table">
            Sets a new password on {email}’s IBM account and emails it to them. Their current password stops working.
          </p>
          {resetPassword.data ? (
            <p className="text-small mt-4">
              {resetPassword.data.emailSent
                ? 'New password set and emailed.'
                : 'New password set, but the email did not send. Check the activity log before trying again.'}
            </p>
          ) : null}
          {resetPassword.isError ? (
            <div className="mt-4">
              <ErrorNotice>
                {errorMessage(resetPassword.error, 'IBM did not accept the new password.')} The member can also use
                “Forgot password” on the sign-in page.
              </ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {dialog === 'active' ? (
        <Modal
          title={isActive ? 'Deactivate member' : 'Activate member'}
          onClose={close}
          footer={
            <>
              <Button
                variant={isActive ? 'destructive' : 'primary'}
                disabled={setActive.isLoading}
                onClick={() => setActive.mutate(undefined, { onSuccess: close })}
              >
                {setActive.isLoading ? 'Working…' : isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            {isActive
              ? 'Blocks sign-in to IBM, which locks the member out of Moodle and the mentorship app. Nothing is deleted, and it can be reversed.'
              : 'Re-enables sign-in to IBM and marks the member active.'}
          </p>
          {setActive.isError ? (
            <div className="mt-4">
              <ErrorNotice>{(setActive.error as Error).message}. Nothing was changed on the platform.</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {dialog === 'name' ? (
        <Modal
          title="Edit name"
          onClose={close}
          footer={
            <>
              <Button
                variant="primary"
                disabled={!names.firstName.trim() || !names.lastName.trim() || changeName.isLoading}
                onClick={() => changeName.mutate(undefined, { onSuccess: close })}
              >
                {changeName.isLoading ? 'Saving…' : 'Save in IBM and here'}
              </Button>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            {(['firstName', 'lastName'] as const).map((field) => (
              <label key={field} className="text-small text-ink-2">
                {field === 'firstName' ? 'First name' : 'Last name'}
                <input
                  value={names[field]}
                  onChange={(e) => setNames({ ...names, [field]: e.target.value })}
                  className="mt-1 w-full border border-line-strong px-3 py-2 text-table text-ink"
                />
              </label>
            ))}
          </div>
          {changeName.isError ? (
            <div className="mt-4">
              <ErrorNotice>{(changeName.error as Error).message}. Nothing was changed on the platform.</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {dialog === 'archive' ? (
        <Modal
          title="Archive member"
          onClose={close}
          footer={
            <>
              <Button
                variant="destructive"
                disabled={!reason.trim() || archive.isLoading}
                onClick={() => archive.mutate(undefined, { onSuccess: close })}
              >
                {archive.isLoading ? 'Archiving…' : 'Delete IBM account and archive'}
              </Button>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            Permanently deletes {email}’s IBM account, so they can no longer sign in to anything. The member record,
            course completions, mentorship and attendance history are kept. For a member who may come back, use
            Deactivate instead.
          </p>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required), e.g. spam registration, duplicate account"
            className="mt-4 w-full border border-line-strong px-3 py-2 text-table"
          />
          {archive.isError ? (
            <div className="mt-4">
              <ErrorNotice>{(archive.error as Error).message}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
